/*
 * VERIFY EMAIL (decision 605): the page a verification link opens.
 *
 * IT VERIFIES AS IT LOADS (Kevin's review). Opening the link IS the act; a
 * second button after it was one click too many, and it was easy to read the
 * page's heading as the result and leave without pressing anything. The rule
 * that a GET must never change state still holds where it matters: the link is
 * a GET of static HTML, and the change is the POST this page makes once it is
 * running. Mail scanners prefetch links; they do not run the SPA.
 *
 * Works signed in or out: the token is the credential, and the link is often
 * opened on a different device from the one that signed up. When this device
 * IS signed in, the app's copy of the user is refreshed afterwards so Settings
 * and the pending screen stop offering to verify an address that now is.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Brand } from "../components/Brand";
import { ApiError, api, type User } from "../lib/api";
import { useLinkToken } from "../lib/linkToken";
import styles from "./SignIn.module.css";

export function VerifyEmail({
  signedIn,
  onUserChanged,
}: {
  signedIn: boolean;
  onUserChanged: (user: User | null) => void;
}) {
  const { t } = useTranslation();
  const token = useLinkToken();
  const [state, setState] = useState<"working" | "done" | "invalid" | "failed">(
    token === null ? "invalid" : "working",
  );

  // ONCE, even where an effect runs twice (React's development StrictMode):
  // the token is single-use, so a second POST would answer "already used" and
  // overwrite the success the first one earned.
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;
    void (async () => {
      try {
        await api.verifyEmail(token);
        setState("done");
        if (signedIn) {
          const me = await api.me().catch(() => null);
          if (me?.user) onUserChanged(me.user);
        }
      } catch (err) {
        setState(
          err instanceof ApiError && err.failure.kind === "server" && err.failure.status === 400
            ? "invalid"
            : "failed",
        );
      }
    })();
  }, [token, signedIn, onUserChanged]);

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <Brand variant="lockup" height={34} />
        </div>

        {state === "done" ? (
          <p className={styles.done} role="status">
            {t("auth.verify.done")}
          </p>
        ) : state === "invalid" ? (
          <>
            <h1 className={styles.heading}>{t("auth.verify.heading")}</h1>
            <p className={styles.intro}>{t("auth.linkInvalid")}</p>
            <p className={styles.intro}>{t("auth.verify.askAgain")}</p>
          </>
        ) : state === "failed" ? (
          <>
            <h1 className={styles.heading}>{t("auth.verify.heading")}</h1>
            <p className={styles.error} role="alert">
              {t("auth.errorGeneric")}
            </p>
          </>
        ) : (
          <p className={styles.intro} role="status">
            {t("auth.verify.verifying")}
          </p>
        )}

        <p className={styles.alt}>
          <Link to="/">{signedIn ? t("auth.continue") : t("auth.backToSignIn")}</Link>
        </p>
      </div>
    </div>
  );
}
